
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as inner from '../inner'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * AccountLoginRangeParams struct
 */
class AccountLoginRangeParams {
  /**
   * Initialize struct
   * @param {!number=} nextLoginInclusive
   * @param {!number=} toLoginExclusive
   * @constructor
   */
  constructor (nextLoginInclusive = new UInt64(0, 0), toLoginExclusive = new UInt64(0, 0)) {
    this.NextLoginInclusive = nextLoginInclusive
    this.ToLoginExclusive = toLoginExclusive
  }

  /**
   * Copy struct (shallow copy)
   * @this {!AccountLoginRangeParams}
   * @param {!AccountLoginRangeParams} other Other struct
   * @returns {!AccountLoginRangeParams} This struct
   */
  copy (other) {
    if (other.NextLoginInclusive != null) {
      this.NextLoginInclusive = UInt64.fromNumber(other.NextLoginInclusive)
    } else {
      this.NextLoginInclusive = null
    }
    if (other.ToLoginExclusive != null) {
      this.ToLoginExclusive = UInt64.fromNumber(other.ToLoginExclusive)
    } else {
      this.ToLoginExclusive = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!AccountLoginRangeParams}
   * @returns {!AccountLoginRangeParams} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new AccountLoginRangeParamsModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new AccountLoginRangeParamsModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!AccountLoginRangeParams}
   * @param {!AccountLoginRangeParams} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof AccountLoginRangeParams)) {
      throw new TypeError('Instance of AccountLoginRangeParams is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!AccountLoginRangeParams}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      NextLoginInclusive: ((this.NextLoginInclusive != null) ? this.NextLoginInclusive.toNumber() : null),
      ToLoginExclusive: ((this.ToLoginExclusive != null) ? this.ToLoginExclusive.toNumber() : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return AccountLoginRangeParams.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!AccountLoginRangeParams} other Object value
   * @returns {!AccountLoginRangeParams} Created struct
   */
  static fromObject (other) {
    return new AccountLoginRangeParams().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!AccountLoginRangeParams}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return AccountLoginRangeParams.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!AccountLoginRangeParams}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 4178
  }
}

export { AccountLoginRangeParams };

/**
 * Fast Binary Encoding AccountLoginRangeParams field model
 */
class FieldModelAccountLoginRangeParams extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.NextLoginInclusive = new fbe.FieldModelUInt64(buffer, 4 + 4)
    this.ToLoginExclusive = new fbe.FieldModelUInt64(buffer, this.NextLoginInclusive.fbeOffset + this.NextLoginInclusive.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelAccountLoginRangeParams}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelAccountLoginRangeParams}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.NextLoginInclusive.fbeSize + this.ToLoginExclusive.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelAccountLoginRangeParams}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.NextLoginInclusive.fbeExtra + this.ToLoginExclusive.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelAccountLoginRangeParams}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelAccountLoginRangeParams.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelAccountLoginRangeParams}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 4178
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelAccountLoginRangeParams}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelAccountLoginRangeParams}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.NextLoginInclusive.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.NextLoginInclusive.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NextLoginInclusive.fbeSize

    if ((fbeCurrentSize + this.ToLoginExclusive.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.ToLoginExclusive.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ToLoginExclusive.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelAccountLoginRangeParams}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelAccountLoginRangeParams}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelAccountLoginRangeParams}
   * @param {!AccountLoginRangeParams} fbeValue Default value, defaults is new AccountLoginRangeParams()
   * @returns {!AccountLoginRangeParams} AccountLoginRangeParams value
   */
  get (fbeValue = new AccountLoginRangeParams()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelAccountLoginRangeParams}
   * @param {!AccountLoginRangeParams} fbeValue AccountLoginRangeParams value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.NextLoginInclusive.fbeSize) <= fbeStructSize) {
      fbeValue.NextLoginInclusive = this.NextLoginInclusive.get()
    } else {
      fbeValue.NextLoginInclusive = new UInt64(0, 0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NextLoginInclusive.fbeSize

    if ((fbeCurrentSize + this.ToLoginExclusive.fbeSize) <= fbeStructSize) {
      fbeValue.ToLoginExclusive = this.ToLoginExclusive.get()
    } else {
      fbeValue.ToLoginExclusive = new UInt64(0, 0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ToLoginExclusive.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelAccountLoginRangeParams}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelAccountLoginRangeParams}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelAccountLoginRangeParams}
   * @param {!AccountLoginRangeParams} fbeValue AccountLoginRangeParams value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelAccountLoginRangeParams}
   * @param {AccountLoginRangeParams} fbeValue AccountLoginRangeParams value
   */
  setFields (fbeValue) {
    this.NextLoginInclusive.set(fbeValue.NextLoginInclusive)
    this.ToLoginExclusive.set(fbeValue.ToLoginExclusive)
  }
}

export { FieldModelAccountLoginRangeParams };

/**
 * Fast Binary Encoding AccountLoginRangeParams model
 */
class AccountLoginRangeParamsModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelAccountLoginRangeParams(this.buffer, 4)
  }

  /**
   * Get the AccountLoginRangeParams model
   * @this {!AccountLoginRangeParamsModel}
   * @returns {!FieldModelAccountLoginRangeParams} model AccountLoginRangeParams model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!AccountLoginRangeParamsModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!AccountLoginRangeParamsModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return AccountLoginRangeParamsModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!AccountLoginRangeParamsModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelAccountLoginRangeParams.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!AccountLoginRangeParamsModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!AccountLoginRangeParamsModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!AccountLoginRangeParamsModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!AccountLoginRangeParamsModel}
   * @param {!AccountLoginRangeParams} value AccountLoginRangeParams value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!AccountLoginRangeParamsModel}
   * @param {!AccountLoginRangeParams} value AccountLoginRangeParams value, defaults is new AccountLoginRangeParams()
   * @return {!object} Deserialized AccountLoginRangeParams value and its size
   */
  deserialize (value = new AccountLoginRangeParams()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new AccountLoginRangeParams(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new AccountLoginRangeParams(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!AccountLoginRangeParamsModel}
   * @param {!number} prev Previous AccountLoginRangeParams model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { AccountLoginRangeParamsModel };
